//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React, { useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { useDebounce } from 'use-debounce';

import { SamplingCatalogProduct } from '@apis/campaign-requests/sampling/products/samplingProductsApiV2Model';
import { ProductStatusMessage } from '@campaigns/components/shared/product-details-modal/details/ProductStatusMessage';
import { ProductDetailsFieldLabels } from '@campaigns/components/shared/product-details-modal/drawer/constants/ProductDetailsModalFieldLabels';
import { CatalogMatchCard } from '@campaigns/components/shared/product-details-modal/drawer/details/CatalogMatchCard';
import { FormField } from '@campaigns/components/shared/product-details-modal/drawer/form/FormField';
import { SEARCH_DEBOUNCE_DELAY } from '@campaigns/constants/TimeConstants';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { PRODUCT_ID_REGEX_RULE } from '@campaigns/constants/sampling/SamplingProductValidationRulesConstants';
import {
  ProductModalMode,
  useProductModal,
} from '@campaigns/contexts/ProductModalContext';
import { useProductSearch } from '@campaigns/hooks/useProductSearch';
import { useSamplingCampaign } from '@campaigns/hooks/useSamplingCampaign';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import { uniqueProductIdValidator } from '@campaigns/utils/sampling/SamplingProductValidatorsUtils';
import { DefaultButton } from '@components/atoms/button/Button';
import { isDefined } from '@utils/filterPredicates';

const CATALOG_MATCH_ERROR =
  'We found a product with this ID in your catalog, select product or use a different Product ID';
const CATALOG_LOOKUP_ERROR =
  'Unable to check this Product ID in your catalog. Please retry.';
const CATALOG_LOOKUP_PENDING =
  'Wait for the catalog lookup to complete before saving.';

interface ProductBasicFieldsProps {
  isViewOnly: boolean;
}

export const ProductBasicFields = ({ isViewOnly }: ProductBasicFieldsProps) => {
  const {
    productModalState,
    setModalMode,
    setShowImagePreview,
    updateProductInModalState,
  } = useProductModal();
  const { samplingCampaignRequest } = useSamplingCampaign();
  const {
    control,
    formState: { errors },
    getValues,
    reset,
    trigger,
  } = useFormContext<EditableBVSamplingProduct>();
  const [hasImportedProduct, setHasImportedProduct] = useState(false);
  const [productIdSearchText, setProductIdSearchText] = useState<
    string | undefined
  >(undefined);

  // The ID is only typed once, when creating: products already in the campaign
  // (created or imported) and catalog imports keep their ID.
  const isProductIdDisabled =
    !!productModalState.product?.targetCatalogProductId;

  const existingProducts = samplingCampaignRequest?.products;

  const validateCampaignProductId = (value: string) =>
    uniqueProductIdValidator(
      value,
      existingProducts ?? [],
      productModalState.product?.targetCatalogProductId,
    );

  const [debouncedProductId] = useDebounce(
    productIdSearchText,
    SEARCH_DEBOUNCE_DELAY,
  );

  const {
    data: catalogProducts,
    isError,
    isFetching: isCheckingCatalog,
    isSuccess,
    refetch,
  } = useProductSearch({
    productIds: debouncedProductId ? [debouncedProductId] : undefined,
    enabled: isDefined(debouncedProductId) && !isProductIdDisabled,
  });

  const handleSelectProduct = (catalogProduct: SamplingCatalogProduct) => {
    if (validateCampaignProductId(catalogProduct.id ?? '') !== true) {
      void trigger('targetCatalogProductId');
      return;
    }

    const currentValues = getValues();

    const transformedProduct: EditableBVSamplingProduct = {
      ...currentValues,
      name: catalogProduct.name,
      description: catalogProduct.description,
      brand: catalogProduct.brand,
      category: catalogProduct.category,
      imgUrl: catalogProduct.imageUrl,
      pdpUrl: catalogProduct.productDetailPageUrl,
      targetCatalogProductId: catalogProduct.id,
      newProduct: false,
      variations: [
        {
          fairMarketValue: catalogProduct.fmv
            ? catalogProduct.fmv.toString()
            : undefined,
          quantity: undefined,
          variantName: undefined,
          imgUrl: catalogProduct.imageUrl,
        },
      ],
    };

    const upcs = catalogProduct.upcs ?? [];
    const eans = catalogProduct.eans ?? [];
    const totalProductIdentifiers = upcs.length + eans.length;

    if (totalProductIdentifiers > 0 && transformedProduct.variations[0]) {
      if (totalProductIdentifiers === 1) {
        // If there's only one identifier, populate it to the GTIN/EAN/UPC field
        transformedProduct.variations[0].logisticsRef = upcs[0] ?? eans[0];
      } else {
        // If there are multiple identifiers, populate them to the GTIN/EAN/UPC for Syndication
        transformedProduct.destinationGTIN = upcs.concat(eans).join(',');
      }
    }

    reset(transformedProduct);

    // Trigger preview immediately for catalog imports
    if (catalogProduct.imageUrl) {
      setShowImagePreview(true);
    }

    setHasImportedProduct(true);
    setProductIdSearchText(undefined);

    updateProductInModalState(
      isDefined(catalogProduct.id) ? catalogProduct.id : '',
    );

    setModalMode(ProductModalMode.Edit);
  };

  const catalogProductData = catalogProducts?.products[0];
  const productId = useWatch({ control, name: 'targetCatalogProductId' });

  const validateProductIdRef = useRef<(value: string) => string | true>(
    () => CATALOG_LOOKUP_PENDING,
  );
  const catalogMatch =
    !isProductIdDisabled &&
    isSuccess &&
    !isCheckingCatalog &&
    isDefined(catalogProductData?.id) &&
    catalogProductData.id.toLowerCase() === productId?.trim().toLowerCase()
      ? catalogProductData
      : undefined;
  const isCurrentProductIdSearch =
    isDefined(debouncedProductId) &&
    debouncedProductId.trim().toLowerCase() === productId?.trim().toLowerCase();
  const hasSuccessfulCurrentLookup =
    isCurrentProductIdSearch &&
    isSuccess &&
    !isCheckingCatalog &&
    isDefined(catalogProducts);

  validateProductIdRef.current = (value) => {
    const campaignValidation = validateCampaignProductId(value);
    if (campaignValidation !== true) {
      return campaignValidation;
    }
    if (isProductIdDisabled || hasImportedProduct) {
      return true;
    }
    if (
      !hasSuccessfulCurrentLookup ||
      debouncedProductId?.trim().toLowerCase() !== value.trim().toLowerCase()
    ) {
      return isCurrentProductIdSearch && isError && !isCheckingCatalog
        ? CATALOG_LOOKUP_ERROR
        : CATALOG_LOOKUP_PENDING;
    }
    return catalogMatch?.id?.toLowerCase() === value.trim().toLowerCase()
      ? CATALOG_MATCH_ERROR
      : true;
  };

  useEffect(() => {
    if (productId && PRODUCT_ID_REGEX_RULE.test(productId)) {
      void trigger('targetCatalogProductId');
    }
  }, [
    catalogMatch?.id,
    debouncedProductId,
    existingProducts,
    hasImportedProduct,
    hasSuccessfulCurrentLookup,
    isCurrentProductIdSearch,
    isCheckingCatalog,
    isError,
    isProductIdDisabled,
    productId,
    trigger,
  ]);

  const productIdError = errors.targetCatalogProductId?.message;
  const shouldShowLookupStatus =
    isCurrentProductIdSearch &&
    (isCheckingCatalog || hasSuccessfulCurrentLookup);

  const handleValueChange = (value: string) => {
    if (isProductIdDisabled || !value || !PRODUCT_ID_REGEX_RULE.test(value)) {
      return;
    }
    setProductIdSearchText(value);
    setHasImportedProduct(false);
  };

  return (
    <Container>
      <div>
        <FormField
          instructions={`Use the product's unique ID as it appears on your site or the unique identifier your team uses.`}
          isDisabled={isProductIdDisabled}
          isViewOnly={isViewOnly}
          label={ProductDetailsFieldLabels.targetCatalogProductId}
          name='targetCatalogProductId'
          onValueChange={handleValueChange}
          placeholder='PID-290926'
          rules={{
            required: {
              value: true,
              message: 'Please enter a product ID',
            },
            pattern: {
              value: PRODUCT_ID_REGEX_RULE,
              message:
                'Product ID must not start with a space or dash, must end with a letter or number, and can only contain letters, numbers, underscores, periods, vertical bars, and dashes',
            },
            validate: (value: string) => validateProductIdRef.current(value),
          }}
          width='full'
        />

        {isCurrentProductIdSearch && isError && !isCheckingCatalog && (
          <DefaultButton
            onClick={() => void refetch()}
            trackingId={
              SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_CATALOG_LOOKUP_RETRY_BUTTON
            }
          >
            Retry
          </DefaultButton>
        )}

        {catalogMatch &&
          validateCampaignProductId(catalogMatch.id ?? '') === true && (
            <CatalogMatchCard
              onSelectProduct={handleSelectProduct}
              product={catalogMatch}
            />
          )}

        {!catalogMatch &&
          (!isDefined(productIdError) ||
            productIdError === CATALOG_LOOKUP_PENDING) &&
          (shouldShowLookupStatus || hasImportedProduct) && (
            <ProductStatusMessage
              catalogProduct={undefined}
              hasImportedProduct={hasImportedProduct}
              isCheckingCatalog={isCheckingCatalog}
              onSelectProduct={handleSelectProduct}
            />
          )}
      </div>

      <FormField
        inputType='text'
        isViewOnly={isViewOnly}
        label={ProductDetailsFieldLabels.name}
        name='name'
        placeholder='Awesome Product Name'
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[3]};
`;
